// Iter XX.216 — Android download CTA. Web-only surface for paid Meta
// traffic landing on askgully.com. Reads UTM + fbclid from the URL and
// forwards them as a Play Store install-referrer string so the founder
// can attribute installs back to campaigns in Play Console.
//
// Iter XX.248 — iOS parity. iPhone / iPad visitors now see the Apple
// App Store link. Same visual placement, same beacon plumbing, but
// analytics are split into `ios_download_cta_*` vs `android_download_cta_*`
// so campaign attribution stays clean per store.
//
// PRE-COMPLIANCE:
//   · Never renders inside the Capacitor native shell (redundant + would
//     surface an external payment path on iOS).
//   · Never renders for authenticated users on Plus (they already have
//     the app or don't need selling to).
//   · Dismissable, session-scoped. Never annoy after dismiss.
//   · Uses the existing `/api/plus/track` beacon — respects the same
//     consent/privacy path as landingAnalytics.js.
import api from "./api";
import { isNative } from "./nativePlatform";

// -- Canonical Play Store destination (founder-supplied XX.216) ----------
export const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.gullyapp.mobile";

// -- Canonical App Store destination (founder-supplied XX.248) -----------
export const IOS_APP_STORE_URL =
  "https://apps.apple.com/ca/app/gully/id6806074167";

const _DISMISS_KEY = "gully_android_dl_banner_dismissed_v1";

// -- Platform detection --------------------------------------------------
// XX.248 — detects iPhone, iPod AND iPadOS 13+ (which advertises as
// Mac but has touch). MSStream check rules out legacy IE-on-Windows-Phone
// spoofing the iOS user-agent.
export function isIOSSafari() {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    if (typeof window !== "undefined" && window.MSStream) return false;
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ Safari reports as "Macintosh" — disambiguate via
    // touch capability which desktop macOS never reports > 1.
    if (
      navigator.platform === "MacIntel" &&
      typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 1
    ) {
      return true;
    }
    return false;
  } catch { return false; }
}

export function isAndroidWeb() {
  try {
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    return /Android/i.test(ua) && !isNative();
  } catch { return false; }
}

// XX.248 — normalised platform key for tracking + copy selection.
// Returns "ios" | "android" | "web" (desktop / other). Never returns
// "native" — CTA doesn't render inside Capacitor anyway.
export function getStorePlatform() {
  if (isIOSSafari()) return "ios";
  if (isAndroidWeb()) return "android";
  return "web";
}

// -- Attribution parameter forwarding ------------------------------------
const _ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
];

export function readAttributionFromLocation() {
  try {
    const params = new URLSearchParams(
      (typeof window !== "undefined" && window.location.search) || "",
    );
    const out = {};
    for (const k of _ATTRIBUTION_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v;
    }
    return out;
  } catch { return {}; }
}

// Play Console's install-referrer accepts an arbitrary URL-encoded query
// string via `?referrer=...`. Keep it short — Play caps at ~1KB.
export function buildPlayStoreUrl(attribution = null) {
  const attr = attribution || readAttributionFromLocation();
  const keys = Object.keys(attr);
  if (!keys.length) return ANDROID_PLAY_STORE_URL;
  const referrer = keys
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(attr[k])}`)
    .join("&");
  const sep = ANDROID_PLAY_STORE_URL.includes("?") ? "&" : "?";
  return `${ANDROID_PLAY_STORE_URL}${sep}referrer=${encodeURIComponent(referrer)}`;
}

/**
 * Return the correct store URL for the current platform. Web (any
 * non-iOS Safari) gets Play; iOS Safari (iPhone / iPod / iPadOS 13+)
 * gets the App Store listing (XX.248). Native shell returns null —
 * caller must not render a CTA inside Capacitor.
 */
export function resolveStoreDestination() {
  if (isNative()) return null;              // native app is already installed
  if (isIOSSafari()) return IOS_APP_STORE_URL;
  return buildPlayStoreUrl();
}

// -- Session-scoped dismiss ---------------------------------------------
export function isBannerDismissed() {
  try {
    return window.sessionStorage.getItem(_DISMISS_KEY) === "1";
  } catch { return false; }
}

export function markBannerDismissed() {
  try { window.sessionStorage.setItem(_DISMISS_KEY, "1"); } catch { /* noop */ }
}

// -- Tracking beacons (reuses /api/plus/track from landingAnalytics.js) --
function _pageContext() {
  try {
    return {
      path: window.location.pathname,
      hostname: window.location.hostname,
      is_android_web: isAndroidWeb(),
      is_ios_safari: isIOSSafari(),
    };
  } catch { return {}; }
}

let _viewedFired = false;

// XX.248 — Route the tracking event through per-platform kinds so the
// founder can filter iOS vs Android CTA performance separately in the
// dashboard. The Android event names are unchanged (`android_download_cta_*`)
// so historical charts + queries keep working. iOS traffic emits
// `ios_download_cta_*` events. Desktop / other visitors keep firing the
// Android event names (the CTA falls back to the Play URL for them).
function _kindFor(base) {
  const p = getStorePlatform();
  if (p === "ios") return `ios_download_cta_${base}`;
  return `android_download_cta_${base}`;
}

export function trackDownloadCtaView({ placement } = {}) {
  // De-duplicate view events within a single page-load so we don't
  // over-count when the CTA remounts on route change. Placement is
  // still recorded so the founder can distinguish where the very
  // first view fired (Iter XX.217).
  if (_viewedFired) return;
  _viewedFired = true;
  try {
    api.post("/plus/track", {
      kind: _kindFor("view"),
      payload: {
        placement: placement || "unknown",
        store_platform: getStorePlatform(),
        ...readAttributionFromLocation(),
        ..._pageContext(),
        ts: Date.now(),
      },
    }).catch(() => {});
  } catch { /* analytics must never break the page */ }
}

export function trackDownloadCtaClick({ placement } = {}) {
  try {
    api.post("/plus/track", {
      kind: _kindFor("click"),
      payload: {
        placement: placement || "unknown",
        store_platform: getStorePlatform(),
        ...readAttributionFromLocation(),
        ..._pageContext(),
        ts: Date.now(),
      },
    }).catch(() => {});
  } catch { /* noop */ }
}

// -- Global visibility gate ---------------------------------------------
/**
 * Whether the download CTA should render at all in the current context.
 * Composable — used by every download surface (sticky banner, section
 * card, etc.) so the gating logic is defined in one place.
 *
 * XX.248: iOS Safari now qualifies (App Store listing live). Name kept
 * as `shouldShowAndroidDownloadCta` for backwards-compat with all
 * call-sites; alias `shouldShowStoreDownloadCta` mirrors the broader
 * semantic for new code.
 */
export function shouldShowAndroidDownloadCta() {
  if (isNative()) return false;
  if (isBannerDismissed()) return false;
  return true;
}

// XX.248 — semantic alias. Same behaviour, broader name.
export const shouldShowStoreDownloadCta = shouldShowAndroidDownloadCta;
